import { Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader } from "@/components";
import type { ApiDeviceBranding } from "../../_types/device-brandings.types";
import { prototypeAssetLabel } from "../../_constants/device-branding-prototypes.const";
import styles from "./BrandDetailDrawer.module.scss";

type BrandDetailDrawerProps = {
  brandId: string | null;
  brands: ApiDeviceBranding[];
  isOpen: boolean;
  onClose: () => void;
  onRequestDelete: (brand: ApiDeviceBranding) => void;
};

export const BrandDetailDrawer = ({
  brandId,
  brands,
  isOpen,
  onClose,
  onRequestDelete,
}: BrandDetailDrawerProps) => {
  const brand = brandId != null ? brands.find((b) => b.id === brandId) : undefined;

  const title = brand?.name ?? "Branding";
  const subtitle =
    brand != null ? `Prototype · Version ${brand.configVersion}` : "Prototype / Preview";

  return (
    <Drawer isOpen={isOpen} onClose={onClose} ariaLabel="Branding details (prototype)">
      <DrawerHeader title={title} subtitle={subtitle} onClose={onClose} />
      <DrawerBody>
        <div className={styles["comp"]}>
          {!brand && brandId != null ? (
            <p className={styles["comp__error"]}>Branding not found in the current prototype list.</p>
          ) : null}

          {brand ? (
            <>
              <p className={styles["comp__hint"]}>
                Prototype / Preview — this branding package exists only in page memory and cannot be
                assigned to real devices.
              </p>
              <div className={styles["comp__grid"]}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div className={styles["comp__label"]}>Description</div>
                  <div className={styles["comp__value"]}>{brand.description ?? "—"}</div>
                </div>
                <div>
                  <div className={styles["comp__label"]}>Display name</div>
                  <div className={styles["comp__value"]}>{brand.displayName ?? "—"}</div>
                </div>
                <div>
                  <div className={styles["comp__label"]}>Accent</div>
                  <div className={styles["comp__value"]}>
                    {brand.accentColor ? (
                      <span
                        className={styles["comp__swatch"]}
                        style={{ background: brand.accentColor }}
                        title={brand.accentColor}
                      />
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
                <div>
                  <div className={styles["comp__label"]}>Wallpaper</div>
                  <div className={styles["comp__value"]}>
                    {prototypeAssetLabel(brand.wallpaperAssetId)}
                  </div>
                </div>
                <div>
                  <div className={styles["comp__label"]}>Logo</div>
                  <div className={styles["comp__value"]}>{prototypeAssetLabel(brand.logoAssetId)}</div>
                </div>
                <div>
                  <div className={styles["comp__label"]}>Alert sound</div>
                  <div className={styles["comp__value"]}>
                    {prototypeAssetLabel(brand.alertSoundAssetId)}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </DrawerBody>
      {brand ? (
        <DrawerFooter>
          <Button type="button" variant="danger" onClick={() => onRequestDelete(brand)}>
            Delete branding
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DrawerFooter>
      ) : null}
    </Drawer>
  );
};
